import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { CheckCircle2 as CheckCircle, Circle, ChevronDown, ChevronUp, Sparkles, ExternalLink, Copy, Plug, Rocket } from "lucide-react";
import { toast } from "sonner";
import PublishingSetupWizard from "./PublishingSetupWizard";

const PLATFORM_LABEL_OVERRIDES = {};

export default function PlatformsTab({ brandId, brand, posts }) {
  const [platforms, setPlatforms] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");
  const [ayrshare, setAyrshare] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = () => api.listBrandPlatforms(brandId).then(setPlatforms);
  useEffect(() => { load(); }, [brandId]);
  useEffect(() => {
    if (brand?.workspace_id) {
      api.ayrshareStatus(brand.workspace_id).then(setAyrshare).catch(() => {});
    }
  }, [brand?.workspace_id, wizardOpen]);

  const visible = platforms.filter((p) => filter === "all" || (filter === "enabled" ? p.enabled : !p.enabled));

  return (
    <div data-testid="platforms-tab">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-3xl tracking-tight">Platforms</h2>
          <p className="text-sm text-[#52525B] mt-1 max-w-2xl">Substack stays the engine — every other platform is engineered to drive readers back. Enable the ones that matter, finish each setup, and the year-generator will adapt every post natively.</p>
        </div>
        <div className="flex gap-1 text-sm">
          {["all", "enabled", "available"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`platform-filter-${f}`} className={`px-3 py-1.5 border text-xs uppercase tracking-wider ${filter === f ? "bg-[#0A0A0B] text-white border-[#0A0A0B]" : "bg-white border-[#E2E4E9] hover:border-black"}`}>{f}</button>
          ))}
        </div>
      </div>

      {/* Ayrshare connection banner */}
      <div className={`mb-4 border-2 p-4 flex items-center justify-between gap-4 flex-wrap ${ayrshare?.connected ? "border-[#059669] bg-[#F0FDF4]" : "border-[#0A0A0B] brutal-shadow bg-[#FFF7ED]"}`} data-testid="ayrshare-banner">
        <div className="flex items-center gap-3">
          {ayrshare?.connected ? <CheckCircle size={22} className="text-[#059669]" fill="#059669" stroke="white" /> : <Rocket size={22} />}
          <div>
            <div className="font-display font-bold text-lg tracking-tight">
              {ayrshare?.connected ? `Ayrshare connected · plan: ${ayrshare.plan || "active"}` : "Want this to actually auto-post for you?"}
            </div>
            <div className="text-xs text-[#52525B] mt-0.5">
              {ayrshare?.connected
                ? `${(ayrshare.active_social_accounts || []).length} accounts connected. Click 'Auto-publish to ALL' on any approved post.`
                : "Wire it once via a 5-step guided wizard. Substack already auto-ships via email; Ayrshare adds IG, FB, X, LinkedIn, TikTok, YouTube, Pinterest, Threads, Reddit."}
            </div>
          </div>
        </div>
        <button onClick={() => setWizardOpen(true)} data-testid="open-publishing-wizard"
                className={`px-5 py-3 text-sm flex items-center gap-2 ${ayrshare?.connected ? "border border-black hover:bg-[#F4F5F6]" : "bg-[#0A0A0B] text-white hover:bg-[#002FA7]"}`}>
          <Plug size={14} /> {ayrshare?.connected ? "Manage connections" : "Set up auto-publishing"}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {visible.map((p) => (
          <PlatformCard
            key={p.id}
            platform={p}
            brandId={brandId}
            brand={brand}
            posts={posts}
            expanded={expanded === p.id}
            onToggle={() => setExpanded(expanded === p.id ? null : p.id)}
            onChanged={load}
          />
        ))}
      </div>

      {wizardOpen && (
        <PublishingSetupWizard
          workspaceId={brand?.workspace_id}
          brandId={brandId}
          onClose={() => setWizardOpen(false)}
          onConnected={() => { setWizardOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function PlatformCard({ platform, brandId, brand, posts, expanded, onToggle, onChanged }) {
  const p = platform;
  return (
    <div className={`border ${p.enabled ? "border-[#0A0A0B]" : "border-[#E2E4E9]"}`} data-testid={`platform-card-${p.id}`}>
      <button onClick={onToggle} className="w-full p-4 text-left flex items-center justify-between hover:bg-[#FAFAFA]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10" style={{ background: p.color }} />
          <div>
            <div className="font-display font-bold tracking-tight">{p.label}</div>
            <div className="text-xs mono uppercase text-[#A1A1AA] mt-0.5">
              {p.category} · {p.enabled ? `enabled · ${p.posts_count} drafts` : "available"} {p.completed_steps?.length > 0 && ` · ${p.completed_steps.length}/${p.setup_steps?.length || 0} setup`}
            </div>
          </div>
        </div>
        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {expanded && (
        <div className="border-t border-[#E2E4E9] p-4 space-y-4">
          <PlatformSettings platform={p} brandId={brandId} onChanged={onChanged} />
          <PlatformSetupWizard platform={p} brandId={brandId} onChanged={onChanged} />
          {p.enabled && <PlatformContentSection platform={p} brandId={brandId} brand={brand} posts={posts} />}
        </div>
      )}
    </div>
  );
}

function PlatformSettings({ platform, brandId, onChanged }) {
  const [data, setData] = useState({
    enabled: platform.enabled || false,
    account_url: platform.account_url || "",
    account_handle: platform.account_handle || "",
    notes: platform.notes || "",
    completed_steps: platform.completed_steps || [],
    has_account: platform.has_account,
  });
  const save = async (changes) => {
    const merged = { ...data, ...changes };
    setData(merged);
    await api.updateBrandPlatform(brandId, platform.id, merged);
    onChanged();
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={data.enabled} onChange={(e) => save({ enabled: e.target.checked })} data-testid={`enable-${platform.id}`} />
          Enable for this brand
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <input
          placeholder="Account URL (e.g. https://instagram.com/yourbrand)"
          value={data.account_url}
          onChange={(e) => setData({ ...data, account_url: e.target.value })}
          onBlur={() => save({})}
          data-testid={`url-${platform.id}`}
          className="border border-[#E2E4E9] focus:border-black p-2 outline-none"
        />
        <input
          placeholder="Handle (e.g. @yourbrand)"
          value={data.account_handle}
          onChange={(e) => setData({ ...data, account_handle: e.target.value })}
          onBlur={() => save({})}
          data-testid={`handle-${platform.id}`}
          className="border border-[#E2E4E9] focus:border-black p-2 outline-none font-mono"
        />
      </div>
      <textarea
        placeholder="Notes for this platform (target subreddits, posting times, hashtags that work…)"
        value={data.notes}
        onChange={(e) => setData({ ...data, notes: e.target.value })}
        onBlur={() => save({})}
        data-testid={`notes-${platform.id}`}
        rows={2}
        className="w-full border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none resize-none"
      />
    </div>
  );
}

function PlatformSetupWizard({ platform, brandId, onChanged }) {
  const completed = new Set(platform.completed_steps || []);
  const toggle = async (stepId) => {
    const next = completed.has(stepId)
      ? (platform.completed_steps || []).filter((s) => s !== stepId)
      : [...(platform.completed_steps || []), stepId];
    await api.updateBrandPlatform(brandId, platform.id, {
      enabled: platform.enabled,
      account_url: platform.account_url,
      account_handle: platform.account_handle,
      notes: platform.notes,
      completed_steps: next,
      has_account: platform.has_account,
    });
    onChanged();
  };
  return (
    <div>
      <div className="text-eyebrow mb-2">Setup wizard</div>
      <div className="space-y-2">
        {platform.setup_steps?.map((s) => {
          const done = completed.has(s.id);
          return (
            <div key={s.id} data-testid={`setup-${platform.id}-${s.id}`} className={`flex items-start gap-2 p-2 border text-sm ${done ? "border-[#059669] bg-[#F0FDF4]" : "border-[#E2E4E9]"}`}>
              <button onClick={() => toggle(s.id)} className="mt-0.5" data-testid={`step-toggle-${platform.id}-${s.id}`}>
                {done ? <CheckCircle size={16} className="text-[#059669]" fill="#059669" stroke="white" /> : <Circle size={16} className="text-[#A1A1AA]" />}
              </button>
              <div className="flex-1">
                <div className={`font-semibold ${done ? "line-through text-[#A1A1AA]" : ""}`}>{s.title}</div>
                <p className="text-xs text-[#52525B] mt-1">{s.detail}</p>
                {s.url && (
                  <a href={s.url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs mono uppercase text-[#002FA7] hover:underline">
                    Open <ExternalLink size={10} />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlatformContentSection({ platform, brandId, brand, posts }) {
  const [platformPosts, setPlatformPosts] = useState([]);
  const [adapting, setAdapting] = useState(false);
  const [selectedSource, setSelectedSource] = useState("");

  const load = () => api.listPlatformPosts(brandId, platform.id).then(setPlatformPosts);
  useEffect(() => { load(); }, [platform.id, brandId]);

  const approved = posts?.filter((p) => p.status === "approved" || p.status === "pending") || [];
  useEffect(() => { if (approved.length && !selectedSource) setSelectedSource(approved[0].post_id); }, [approved.length]);

  const adapt = async () => {
    if (!selectedSource) return toast.error("Pick a source Substack post first");
    setAdapting(true);
    try {
      await api.adaptPostToPlatforms(selectedSource, [platform.id]);
      toast.success(`Adapted for ${platform.label}`);
      load();
    } catch (e) {
      toast.error("Adaptation failed");
    } finally {
      setAdapting(false);
    }
  };

  return (
    <div>
      <div className="text-eyebrow mb-2">Generate {platform.label} content from a Substack post</div>
      <div className="flex gap-2 items-center mb-3">
        <select value={selectedSource} onChange={(e) => setSelectedSource(e.target.value)}
                data-testid={`source-select-${platform.id}`}
                className="flex-1 border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none">
          <option value="">— pick a Substack post —</option>
          {approved.map((p) => <option key={p.post_id} value={p.post_id}>{p.title}</option>)}
        </select>
        <button onClick={adapt} disabled={adapting || !selectedSource} data-testid={`adapt-${platform.id}`}
                className="bg-[#0A0A0B] text-white px-3 py-2 text-xs hover:bg-[#002FA7] disabled:opacity-50 flex items-center gap-1">
          <Sparkles size={12} /> {adapting ? "Adapting…" : "Adapt"}
        </button>
      </div>
      <div className="text-xs text-[#52525B] mb-2">Format rules: {platform.format_rules}</div>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {platformPosts.length === 0 ? (
          <p className="text-xs text-[#A1A1AA]">No adaptations yet.</p>
        ) : (
          platformPosts.map((pp) => <PlatformPostCard key={pp.platform_post_id} pp={pp} platform={platform} onDelete={load} />)
        )}
      </div>
    </div>
  );
}

function PlatformPostCard({ pp, platform, onDelete }) {
  const [open, setOpen] = useState(false);
  const copy = () => {
    let text = pp.primary_text || "";
    if (pp.thread_or_slides?.length) text += "\n\n— Thread/slides —\n" + pp.thread_or_slides.map((t, i) => `${i + 1}. ${t}`).join("\n");
    if (pp.video_script) text += "\n\n— Video script —\n" + pp.video_script;
    if (pp.hashtags?.length) text += "\n\n" + pp.hashtags.map((h) => h.startsWith("#") ? h : `#${h}`).join(" ");
    if (pp.cta) text += "\n\n" + pp.cta;
    navigator.clipboard.writeText(text);
    toast.success(`Copied — paste into ${platform.label}`);
  };
  const remove = async () => {
    await api.deletePlatformPost(pp.platform_post_id);
    onDelete();
  };
  return (
    <div className="border border-[#E2E4E9] p-2 text-xs">
      <div className="flex items-center justify-between">
        <button onClick={() => setOpen(!open)} className="text-left flex-1 hover:underline" data-testid={`pp-${pp.platform_post_id}`}>
          <span className="mono text-[#A1A1AA]">{(pp.scheduled_date || "").slice(0, 10)}</span>
          <span className="ml-2 line-clamp-1">{(pp.primary_text || "").slice(0, 80) || "(empty)"}</span>
        </button>
        <div className="flex gap-1">
          <button onClick={copy} className="text-[#002FA7] hover:underline" data-testid={`copy-pp-${pp.platform_post_id}`}><Copy size={12} /></button>
          <button onClick={remove} className="text-[#E11D48] hover:underline">×</button>
        </div>
      </div>
      {open && (
        <div className="mt-2 space-y-2 bg-[#FAFAFA] p-2">
          {pp.primary_text && <div className="whitespace-pre-wrap">{pp.primary_text}</div>}
          {pp.thread_or_slides?.length > 0 && (
            <div>
              <div className="text-eyebrow">Thread / Slides</div>
              <ol className="list-decimal pl-5 space-y-1">
                {pp.thread_or_slides.map((t, i) => <li key={i}>{t}</li>)}
              </ol>
            </div>
          )}
          {pp.video_script && (
            <div>
              <div className="text-eyebrow">Video script</div>
              <pre className="whitespace-pre-wrap font-mono">{pp.video_script}</pre>
            </div>
          )}
          {pp.hashtags?.length > 0 && <div className="text-[#002FA7]">{pp.hashtags.map((h) => h.startsWith("#") ? h : `#${h}`).join(" ")}</div>}
          {pp.cta && <div className="font-semibold">{pp.cta}</div>}
          {pp.image_brief && <details><summary className="cursor-pointer text-[#A1A1AA] uppercase mono">Image brief</summary><p className="mt-1">{pp.image_brief}</p></details>}
        </div>
      )}
    </div>
  );
}
